import { DestroyRef, Injectable } from '@angular/core';
import { ElementButtonService, ResizeService, CreatorActionsService, CreatorStateService } from '../services/app-services';
import { GuidWrapper } from '../types';
import { ISeat } from '../interfaces';
import { SeatManager } from './seat.manager';
import { ShapeAttributesManager } from './shape-attributes.manager';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DragStates, ResizeEvents } from '../enums';
import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash';

@Injectable()
export class LoveSeatManager {

  public static loveSeatResized$ = new Subject<{
    previous: GuidWrapper<ISeat>[],
    current: GuidWrapper<ISeat>[]
  }>();
  public static data: {
    loveSeat: GuidWrapper<ISeat>,
    originalWidth: number,
    originalHeight: number,
    originalX: number,
    originalY: number
  }[] = [];
  public static loveSeatResizeService: ResizeService;
  public static elementButtonService: ElementButtonService;
  private static _destroyRef: DestroyRef;

  public static withResize(resizeService: ResizeService): void {
    LoveSeatManager.loveSeatResizeService = resizeService;
    LoveSeatManager.loveSeatResizeService.init('external-selection');

    LoveSeatManager.loveSeatResizeService.afterMouseDown$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data) => {
        const loveSeats = LoveSeatManager.getAllLoveSeatsByLoveSeatId(data.el.id).sort((a, b) => a.sectorElement.webX - b.sectorElement.webX);

        for (const loveSeat of loveSeats) {
          const seatEl = SeatManager.getHTMLElement(loveSeat.guid);

          this.data.push({
            loveSeat,
            originalWidth: seatEl.offsetWidth,
            originalHeight: seatEl.offsetHeight,
            originalX: seatEl.offsetLeft,
            originalY: seatEl.offsetTop
          });
        }
      });

    LoveSeatManager.loveSeatResizeService.afterMouseMove$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data) => {
        if (data.el) {
          this.removeButtons(data.el);
        }
      });

    LoveSeatManager.loveSeatResizeService.afterMouseMove$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data) => {
        const length = this.data.length;
        const width = (data.current.width - data.previous.width) / length;
        const height = data.current.height - data.previous.height;
        const webY = data.current.webY - data.previous.webY;

        for (let i = 0; i < length; i++) {
          const value = this.data[i];
          const seatEl = SeatManager.getHTMLElement(value.loveSeat.guid);

          seatEl.style.width = `${value.originalWidth + width}px`;
          seatEl.style.height = `${value.originalHeight + height}px`;

          switch (data.resizeAction) {
            case ResizeEvents.TOP_LEFT:
              seatEl.style.top = `${value.originalY + webY}px`;
              seatEl.style.left = `${value.originalX - (width * (this.data.length - i))}px`;
              break;
            case ResizeEvents.TOP_RIGHT:
              seatEl.style.top = `${value.originalY + webY}px`;
              seatEl.style.left = `${value.originalX + (width * i)}px`;
              break;
            case ResizeEvents.BOTTOM_LEFT:
              seatEl.style.left = `${value.originalX - (width * (this.data.length - i))}px`;
              break;
            case ResizeEvents.BOTTOM_RIGHT:
              seatEl.style.left = `${value.originalX + (width * i)}px`;
              break;
          }
        }
      });

    LoveSeatManager.loveSeatResizeService.elementResized$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data) => {
        const seatsToUpdate: GuidWrapper<ISeat>[] = [];
        const previous: GuidWrapper<ISeat>[] = [];

        for (const value of this.data) {
          const seatEl = SeatManager.getHTMLElement(value.loveSeat.guid);
          const seat = value.loveSeat;

          previous.push(cloneDeep(seat));

          seat.sectorElement.w = seatEl.offsetWidth;
          seat.sectorElement.h = seatEl.offsetHeight;
          seat.sectorElement.webX = seatEl.offsetLeft;
          seat.sectorElement.webY = seatEl.offsetTop;

          seatsToUpdate.push(seat);
        }

        const uSeats = CreatorActionsService.updateSeats(seatsToUpdate);
        this.loveSeatResized$.next({
          previous,
          current: uSeats
        });
        this.data = [];

        setTimeout(this.createSelection.bind(this, data.el.id));
      });
  }

  public static getSelection(loveSeatId: string): HTMLElement {
    return CreatorStateService.roomRef.nativeElement.querySelector(`.external-selection#${CSS.escape(loveSeatId)}`);
  }

  public static withDestroyRef(destroyRef: DestroyRef): void {
    this._destroyRef = destroyRef;
  }

  public static withButtons(buttonService: ElementButtonService): void {
    this.elementButtonService = buttonService;
  }

  public static removeButtons(selection: HTMLElement): void {
    selection.querySelectorAll('.mid-love-seat-button, .add-love-seat').forEach((btn) => btn.remove());
  }

  public static getAllLoveSeatsByLoveSeatId(loveSeatId: string): GuidWrapper<ISeat>[] {
    return CreatorStateService.seatsPlan.seats.filter(x => x.loveSeatId === loveSeatId);
  }

  public static getAllLoveSeatsElementsByLoveSeatId(loveSeatId: string): HTMLElement[] {
    return this.getAllLoveSeatsByLoveSeatId(loveSeatId).map(x => SeatManager.getHTMLElement(x.guid));
  }

  public static createSelection(loveSeatId: string, shouldCreateButtons = true, container?: HTMLElement): HTMLElement {
    const seats = LoveSeatManager.getAllLoveSeatsByLoveSeatId(loveSeatId).sort((x, y) => x.sectorElement.webX - y.sectorElement.webX);
    const seatElements: HTMLElement[] = [];

    if (!seats.length) {
      return null;
    }

    const fSelection = (container || CreatorStateService.roomRef.nativeElement)?.querySelector(`#${CSS.escape(loveSeatId)}`);

    if (fSelection) {
      fSelection.remove();
    }

    let topElement = seats[0];
    let bottomElement = seats[0];
    let leftElement = seats[0];
    let rightElement = seats[0];

    for (const seat of seats) {
      if (seat.sectorElement.webY < topElement.sectorElement.webY) {
        topElement = seat;
      }

      if ((seat.sectorElement.webY + seat.sectorElement.h) > (bottomElement.sectorElement.webY + bottomElement.sectorElement.h)) {
        bottomElement = seat;
      }

      if (seat.sectorElement.webX < leftElement.sectorElement.webX) {
        leftElement = seat;
      }

      if ((seat.sectorElement.webX + seat.sectorElement.w) > (rightElement.sectorElement.webX + rightElement.sectorElement.w)) {
        rightElement = seat;
      }

      const seatEl = SeatManager.getHTMLElement(seat.guid);

      if (seatEl) {
        seatElements.push(seatEl);
        ShapeAttributesManager.setDragState(seatEl, DragStates.SELECTED);
        ShapeAttributesManager.setActiveExternalSelection(seatEl, 1);
      }
    }

    const selection = document.createElement('div');
    selection.className = 'external-selection';
    selection.id = loveSeatId;
    selection.style.top = `${topElement.sectorElement.webY}px`;
    selection.style.left = `${leftElement.sectorElement.webX}px`;
    selection.style.width = `${rightElement.sectorElement.webX + rightElement.sectorElement.w - leftElement.sectorElement.webX}px`;
    selection.style.height = `${bottomElement.sectorElement.webY + bottomElement.sectorElement.h - topElement.sectorElement.webY}px`;
    (container ?? CreatorStateService.roomRef.nativeElement).appendChild(selection);

    if (this.loveSeatResizeService && !CreatorStateService.seatsPlan.isForNewCashApp) {
      this.addResizePoints(selection);
    }

    this.addMiddleButtons(seatElements, selection, loveSeatId);

    if (this.elementButtonService && shouldCreateButtons) {
      this.elementButtonService.addButtonsToElement(selection, 'horizontal', loveSeatId);
    }

    return selection;
  }

  private static addResizePoints(selection: HTMLElement): void {
    this.loveSeatResizeService.createResizePoints(selection);
  }

  private static addMiddleButtons(elements: HTMLElement[], selection: HTMLElement, loveSeatId: string): void {
    const buttonDims = { w: 16, h: 16 };

    for (let i = 0; i < elements?.length; i++) {
      if (i === elements.length - 1) break;
      const seat = elements[i];
      const nextSeat = elements[i + 1];

      const gap = nextSeat.offsetLeft - (seat.offsetLeft + seat.offsetWidth);
      const offsetLeft = seat.offsetLeft - selection.offsetLeft;

      const button = document.createElement('button');
      button.classList.add('mid-love-seat-button');
      button.style.position = 'absolute';
      button.style.width = `${buttonDims.w}px`;
      button.style.height = `${buttonDims.h}px`;
      button.style.left = `${offsetLeft + seat.offsetWidth + (gap / 2) - (buttonDims.w / 2) - 1}px`;
      button.style.top = `${(seat.offsetHeight / 2) - (buttonDims.h / 2)}px`;
      button.setAttribute('data-love-seat-id', loveSeatId);
      button.innerHTML = '<i class="ri-add-line"></i>';

      selection.appendChild(button);
    }
  }
}
